import { Injectable, inject } from '@angular/core';
import type { AppPlugin } from '@capacitor/app';
import type { StatusBarPlugin, Style } from '@capacitor/status-bar';
import type { SplashScreenPlugin } from '@capacitor/splash-screen';

import { isCapacitor } from '@shared/utils/platform.utils';
import { MobileUpdaterService } from './mobile-updater.service';

/** Clase CSS de gancho para estilos exclusivos de la app nativa (Capacitor). */
export const CAP_NATIVE_BODY_CLASS = 'cap-native';

/** Clase CSS que `LayoutService`/toggle de tema aplican al `<html>` en modo oscuro. */
const DARK_MODE_CLASS = 'app-dark';

/**
 * Orquesta la inicialización de los plugins nativos base de Capacitor (móvil):
 * botón atrás de Android, barra de estado y splash screen.
 *
 * En **web** (`isCapacitor()` false) `init()` es un no-op total: no toca el DOM,
 * no registra listeners ni importa ningún plugin. Así el bundle web nunca paga
 * el costo ni el riesgo de estos plugins.
 *
 * Los plugins se cargan con `import()` dinámico (no se importan estáticamente
 * en este archivo, solo sus *tipos*) para no engordar el bundle web, y cada
 * carga/llamada está envuelta en try/catch: un plugin ausente o que falla
 * nunca debe romper el arranque de la app.
 *
 * **Testabilidad:** los `import()` dinámicos están detrás de métodos `protected`
 * (`loadAppPlugin`, `loadStatusBarPlugin`, `loadSplashScreenPlugin`) para que los
 * specs los sobrescriban con una subclase de prueba en vez de depender de
 * `vi.mock` (que no es fiable cuando la suite comparte bundle, ver
 * `docs/trackers/TESTING_BACKLOG.md`).
 */
@Injectable({ providedIn: 'root' })
export class MobileShellService {
  private themeObserver?: MutationObserver;
  private readonly mobileUpdaterService = inject(MobileUpdaterService);

  async init(): Promise<void> {
    if (!isCapacitor()) return;

    document.body.classList.add(CAP_NATIVE_BODY_CLASS);

    await this.initBackButton();
    await this.initStatusBar();
    await this.hideSplashScreen();

    // Auto-update self-hosted del bundle web (ver `MobileUpdaterService`). No bloquea el
    // arranque: notifyAppReady/chequeo de manifest corren en paralelo al resto de la app.
    void this.mobileUpdaterService.init();
  }

  /**
   * Punto de carga dinámica del plugin `@capacitor/app` (sobreescribible en tests).
   *
   * `App` es un *proxy* del puente nativo: cualquier acceso a una propiedad (incluido
   * `then`) dispara una llamada nativa. Si esta función `async` resolviera el proxy
   * directamente, el motor de JS comprobaría "es thenable" leyendo `App.then()` → error
   * nativo `"App.then() is not implemented"` y la promesa queda colgada para siempre.
   * Por eso el proxy viaja SIEMPRE envuelto en un objeto plano (`{ plugin }`).
   */
  protected async loadAppPlugin(): Promise<{ plugin: AppPlugin }> {
    const { App } = await import('@capacitor/app');
    return { plugin: App };
  }

  /**
   * Punto de carga dinámica del plugin `@capacitor/status-bar` (sobreescribible en tests).
   * Mismo motivo que `loadAppPlugin`: el proxy nativo no puede ser el valor resuelto de
   * una promesa.
   */
  protected async loadStatusBarPlugin(): Promise<{ plugin: StatusBarPlugin }> {
    const { StatusBar } = await import('@capacitor/status-bar');
    return { plugin: StatusBar };
  }

  /**
   * Punto de carga dinámica del plugin `@capacitor/splash-screen` (sobreescribible en tests).
   * Mismo motivo que `loadAppPlugin`.
   */
  protected async loadSplashScreenPlugin(): Promise<{ plugin: SplashScreenPlugin }> {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    return { plugin: SplashScreen };
  }

  /**
   * Botón atrás de Android: navega hacia atrás en el historial del router si es
   * posible; si no hay a dónde volver, minimiza la app (comportamiento nativo
   * esperado, en vez de cerrarla o dejarla "atascada").
   */
  private async initBackButton(): Promise<void> {
    try {
      const { plugin: App } = await this.loadAppPlugin();
      await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          App.minimizeApp().catch(() => {
            console.warn('[MobileShell] No se pudo minimizar la app.');
          });
        }
      });
    } catch {
      console.warn('[MobileShell] No se pudo inicializar el botón atrás de Android.');
    }
  }

  /**
   * Estilo de la barra de estado según el tema activo (`.app-dark` en `<html>`),
   * mantenido en sincronía observando cambios en la clase.
   */
  private async initStatusBar(): Promise<void> {
    try {
      const { plugin: StatusBar } = await this.loadStatusBarPlugin();
      await this.applyStatusBarStyle(StatusBar);

      this.themeObserver = new MutationObserver(() => {
        this.applyStatusBarStyle(StatusBar).catch(() => {
          console.warn('[MobileShell] No se pudo actualizar el estilo de la barra de estado.');
        });
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    } catch {
      console.warn('[MobileShell] No se pudo inicializar la barra de estado.');
    }
  }

  private async applyStatusBarStyle(StatusBar: StatusBarPlugin): Promise<void> {
    try {
      // Valores literales del enum `Style` de `@capacitor/status-bar` (DARK = texto claro,
      // para fondos oscuros; LIGHT = texto oscuro, para fondos claros). Se evita un segundo
      // `import()` del plugin solo para leer el enum — mantiene el método fácil de testear.
      const isDark = document.documentElement.classList.contains(DARK_MODE_CLASS);
      await StatusBar.setStyle({ style: (isDark ? 'DARK' : 'LIGHT') as Style });
    } catch {
      console.warn('[MobileShell] No se pudo aplicar el estilo de la barra de estado.');
    }
  }

  private async hideSplashScreen(): Promise<void> {
    try {
      const { plugin: SplashScreen } = await this.loadSplashScreenPlugin();
      await SplashScreen.hide();
    } catch {
      console.warn('[MobileShell] No se pudo ocultar el splash screen nativo.');
    }
  }
}
